import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';

@Directive({
    selector: '[appFocusTrap]',
    standalone: true,
})
export class FocusTrapDirective implements AfterViewInit, OnDestroy {
    private firstFocusableElement: HTMLElement | null = null;
    private lastFocusableElement: HTMLElement | null = null;
    private focusableElements: NodeListOf<HTMLElement> | null = null;
    private mutationObserver: MutationObserver;

    private _tabIndex: number = 0;

    /**
     * Controls whether the container on which the directive is applied has to be accessible by Tab/Shift+Tab
     *
     * If true, the users would be able to focus this container (the one on which the directive is applied) by pressing Tab/Shift+Tab
     *
     * @default: false
     */
    @Input({ required: true }) isContainerFocusableByKeyboard: boolean = false;

    /**
     * The index of the modal or the element representing the order of the layers in which these containers are displayed.
     *
     * Used to focus the correct element, and on closing/destroying, to attempt to return the focus to the next layer (the one "behind" the element that's closed)
     * @param value
     */
    @Input({ required: true }) set tabIndex(value: number) {
        if (value === undefined || value === null) {
            value = 0;
        }

        this._tabIndex = this.isContainerFocusableByKeyboard ? Math.abs(value) + 1 : -Math.abs(value) - 1;
    }

    get tabIndex(): number {
        return this._tabIndex;
    }

    constructor(private el: ElementRef<HTMLElement>) {
        this.mutationObserver = new MutationObserver(() =>
            // Timeout OK
            setTimeout(() => {
                this.getFocusableElements();
            }, 500),
        );
    }

    ngAfterViewInit() {
        // Make the modal container focusable
        this.el.nativeElement.setAttribute('tabindex', this.tabIndex.toString());

        // Now, focus the modal container
        this.el.nativeElement.focus();

        this.getFocusableElements();
        this.mutationObserver.observe(this.el.nativeElement, {
            childList: true,
            subtree: true,
        });
    }

    getFocusableElements() {
        this.focusableElements = this.el.nativeElement.querySelectorAll(`
            a[href]:not([disabled]), button:not([disabled]), div[tabindex="0"], label[tabindex="0"],
            span[tabindex="0"], textarea:not([disabled]), input[type="text"]:not([disabled]),
            input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]),
            input[type="decimals"]:not([disabled]), select:not([disabled])
        `) as NodeListOf<HTMLElement>;

        this.firstFocusableElement = this.focusableElements?.[0] ?? null;
        this.lastFocusableElement = this.focusableElements?.[this.focusableElements.length - 1] ?? null;
    }

    @HostListener('keydown', ['$event']) onKeydown(event: KeyboardEvent) {
        if (event.key !== 'Tab') {
            return;
        }

        if (event.shiftKey && document.activeElement === this.firstFocusableElement) {
            // Shift + Tab on the first focusable element, move focus to the last focusable element
            this.lastFocusableElement?.focus();
            event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === this.lastFocusableElement) {
            // Tab on the last focusable element, move focus to the first focusable element
            this.firstFocusableElement?.focus();
            event.preventDefault();
        }
        // For all other cases, let the event propagate
    }

    ngOnDestroy() {
        //Attempt to return the focus to the next open modal or focusable element
        //Our purpose is to try and get closer to a tabIndex value of 0, since these modals are displayed in a for loop,
        // and as input we receive their position in the loop
        if (this.getNextTabIndex() > 0) {
            const nextFocusableElement: NodeListOf<HTMLElement> = document.body.querySelectorAll(
                `div[tabindex="${this.getNextTabIndex()}"]`,
            );
            nextFocusableElement.item(0)?.focus();
        }

        // If the tabindex is 0, there are no modals open, so we don't attempt to return the focus to a specific element, and we only disconnect the observer
        this.mutationObserver.disconnect();
    }

    private getNextTabIndex() {
        if (this.tabIndex < 0) {
            return this.tabIndex + 1;
        }

        if (this.tabIndex > 0) {
            return this.tabIndex - 1;
        }

        return this.tabIndex;
    }
}
